import { TodoItem } from '../types/todo';

/**
 * Parse tags from various formats (string, string[], or undefined) into a clean string array
 */
export function parseTags(tags: string | string[] | undefined): string[] {
  if (!tags) return [];
  
  if (Array.isArray(tags)) {
    return tags.filter(tag => tag && tag.trim().length > 0).map(tag => tag.trim());
  }
  
  // Handle old comma-separated string format for backward compatibility
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
  }
  
  return [];
}

/**
 * Format tags for display - validates and cleans the array
 */
export function formatTagsForDisplay(tags: string[] | undefined): string[] {
  if (!tags || !Array.isArray(tags)) return [];
  
  return tags
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0)
    .filter((tag, index, array) => array.indexOf(tag) === index) // Remove duplicates
    .slice(0, 10); // Limit to 10 tags for performance
}

/**
 * Get a consistent color for a tag based on its content
 */
export function getTagColor(tag: string): string {
  if (!tag) return 'gray';
  
  // Hash the tag name to get a consistent color
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    const char = tag.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Map to Mantine color options
  const colors = [
    'blue', 'green', 'red', 'orange', 'purple', 'teal', 
    'cyan', 'pink', 'indigo', 'violet', 'grape', 'lime'
  ];
  
  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
}

/**
 * Extract all unique tags from a user's todos for autocomplete suggestions
 */
export function getAllUserTags(todos: TodoItem[]): string[] {
  if (!Array.isArray(todos)) return [];
  
  const allTags = new Set<string>();
  
  todos.forEach(todo => {
    const todoTags = parseTags(todo.tags);
    todoTags.forEach(tag => {
      if (tag && tag.trim().length > 0) {
        allTags.add(tag.trim().toLowerCase());
      }
    });
  });
  
  return Array.from(allTags).sort();
}

/**
 * Validate a tag for creation
 */
export function isValidTag(tag: string): boolean {
  if (!tag || typeof tag !== 'string') return false;
  
  const trimmed = tag.trim();
  
  // Check length (1-50 characters)
  if (trimmed.length === 0 || trimmed.length > 50) return false;
  
  // Check for invalid characters (allow alphanumeric, spaces, hyphens, underscores)
  const validPattern = /^[a-zA-Z0-9\s\-_]+$/;
  if (!validPattern.test(trimmed)) return false;
  
  return true;
}

/**
 * Clean and validate a tag for saving
 */
export function cleanTag(tag: string): string {
  if (!tag || typeof tag !== 'string') return '';
  
  return tag
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .substring(0, 50); // Limit length
}

/**
 * Format tags for API submission (convert array to format expected by backend)
 */
export function formatTagsForApi(tags: string[]): string[] {
  return formatTagsForDisplay(tags).map(cleanTag);
}

/**
 * Get tag statistics for a todo list
 */
export interface TagStats {
  tag: string;
  count: number;
  color: string;
}

export function getTagStats(todos: TodoItem[]): TagStats[] {
  if (!Array.isArray(todos)) return [];
  
  const tagCounts = new Map<string, number>();
  
  todos.forEach(todo => {
    const todoTags = parseTags(todo.tags);
    todoTags.forEach(tag => {
      const cleanedTag = cleanTag(tag);
      if (cleanedTag) {
        tagCounts.set(cleanedTag, (tagCounts.get(cleanedTag) || 0) + 1);
      }
    });
  });
  
  return Array.from(tagCounts.entries())
    .map(([tag, count]) => ({
      tag,
      count,
      color: getTagColor(tag)
    }))
    .sort((a, b) => b.count - a.count); // Sort by usage frequency
}